/**
 * How a round's date reads, given what its source actually knew.
 *
 * Dealroom dates a round to a year and a month and no day, so `announced_on`
 * stores the first of the month and that day is padding. Rendering it states a
 * fact nobody has: every September round would read as "1 Sep", which is both
 * wrong and confidently wrong. A month-precision round therefore shows its
 * month and its year and stops there.
 *
 * The day a round did arrive on is `first_seen_at`, which is a separate
 * question with a separate column.
 */
/**
 * Sources that date a round to a month rather than to a day. A round somebody
 * adds by hand is asked for its month and nothing finer, because that is the
 * precision everybody quotes a round at.
 */
const MONTH_PRECISION = new Set([
    'dealroom',
    'manual',
]);
export function isMonthPrecision(source) {
    return MONTH_PRECISION.has(source);
}
/**
 * Fixed rather than `Intl`: this string is read by a model through MCP and by a
 * person in a browser, and the two must not disagree because one of them had a
 * different locale.
 */
const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
/** `investment.date_precision` is text; anything but `year` is the column's default. */
export function datePrecisionOf(value) {
    return value === 'year' ? 'year' : 'month';
}
/**
 * "Sep 2026" for a round dated to a month, "3 Sep 2026" for one dated to a
 * day, "2019" for one dated only to its year.
 * The year is always there: a feed reaching back two years and reading "3 Sep"
 * is a list nobody can order by eye.
 *
 * The parts are read off the string rather than through `Date`, because a
 * `date` has no zone and parsing one into a `Date` walks the day backwards
 * everywhere east of Greenwich.
 */
export function formatRoundDate(round) {
    if (round.announcedOn === null)
        return '';
    const [year, month, day] = round.announcedOn.split('-');
    if (year === undefined || month === undefined)
        return '';
    if (round.datePrecision === 'year')
        return year;
    const name = MONTHS[Number(month) - 1];
    if (name === undefined)
        return '';
    if (isMonthPrecision(round.source))
        return `${name} ${year}`;
    return day === undefined ? `${name} ${year}` : `${Number(day)} ${name} ${year}`;
}
/**
 * Words Dealroom's round vocabulary shouts that are acronyms rather than
 * words. Everything else in a label is a word, and a two-letter run is a
 * series letter or an acronym either way.
 */
const ROUND_ACRONYMS = new Set(['IPO', 'VC', 'SPAC', 'LBO', 'ICO']);
/**
 * Dealroom writes its round labels in capitals — "SERIES A", "POST IPO DEBT".
 * Shouted at the reader once a row they read as noise, so the casing is
 * softened and nothing else is: an unknown term keeps its own words, because
 * guessing at what "LATE VC" means is a different thing from not shouting it.
 */
export function formatRoundLabel(label) {
    if (label === null)
        return '';
    return label
        .split(/\s+/)
        .filter((word) => word !== '')
        .map((word) => word.length <= 2 || ROUND_ACRONYMS.has(word.toUpperCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
/** "Sep 2026" from the `YYYY-MM` a recent investment is dated to. */
export function formatRoundMonth(month) {
    return formatRoundDate({
        announcedOn: `${month}-01`,
        source: 'dealroom',
        datePrecision: 'month',
    });
}
//# sourceMappingURL=roundDate.js.map